import React, { createRef } from 'react'
import autosize from 'autosize'
import SimpleBar from 'simplebar-react'
import { cloneDeep, isEqual, set } from 'lodash'
import { withTranslation } from 'react-i18next'
import 'simplebar/dist/simplebar.min.css'

import Toast from '../../../../../../../common/components/Toast/Toast'
import Modal from '../../../../../../../common/components/Modal/Modal'
import Header from '../../../../../../../common/components/Modal/components/Header/Header'
import Footer from '../../../../../../../common/components/Modal/components/Footer/Footer'
import StopPanel from '../../../../../../../common/components/Modal/components/StopPanel/StopPanel'

import { TRIVIA_TABS_ARRAY } from './constants'

import getRandomID from '../../../../../../../utils/getRandomId'
import moveInArray from '../../../util/moveInArray'

import VC from '../../../util/versionControl'
import NavigationListBar from '../../Controls/NavigationListBar'
import { SCHEMAS, VALIDATION_RULES } from './schema'
import TAB from './TabEnum'

import './TriviaQuizModal.scss'
import { getNormalizedStructure } from './utilities'
import QuestionList from './views/QuestionList'
import ResultList from './views/ResultList'
import StartScreenView from './views/StartView'

import iconGirl from '../i/girl.svg'

const FIXED_TOP_OFFSET_FOR_SCROLL = 36

class TriviaQuizModal extends React.Component {
    _isMounted = false

    constructor(props) {
        super(props)
        let hasError = false
        let error = { title: '', message: '' }
        const _structure = cloneDeep(props.data.structure)
        let tmpStructure = _structure
        const isEmpty = Object.keys(_structure).length === 0
        const isSupportedVersion = _structure._v && SCHEMAS.structure.versionsList.includes(_structure._v)
        if (!isSupportedVersion) {
            hasError = true
            error.title = 'Initialization error'
            error.message = 'It looks like this project version has become unsupported. Clean structure created'
        }
        const isRequireMigration = _structure._v !== SCHEMAS.structure.version
        if (isSupportedVersion && isRequireMigration && !isEmpty) {
            const _VC = new VC(SCHEMAS.structure.versionsList, SCHEMAS.structure.migrationTree)
            const migration = _VC.migrate(_structure, SCHEMAS.structure.version, '_v')
            if (migration.isSuccess) {
                tmpStructure = migration.structure
            } else {
                hasError = true
                error.title = 'Conversion error'
                error.message = 'Cannot convert the project structure to working version. Clean structure created'
            }
        }

        if (hasError || isEmpty) tmpStructure = this.createNewProject()

        const { t } = props

        this.state = {
            ready: true,
            saving: false,

            activeTab: 1,
            tabsValidationResult: {
                showStopPanel: false,
                onlyActiveTab: false,
                errors: [],
            },

            quitAcceptIsOpen: false,

            initialStructure: props.data.structure,
            structure: _structure,
            tmpStructure: tmpStructure,
            focusedQuestionId: this.findFirstQuestionId(tmpStructure),
            focusedResultId: this.findFirstResultId(tmpStructure),

            hasError: hasError,
            error: error,
        }
        this.scrollableQuestionContentNodeRef = createRef()
        this.scrollableResultContentNodeRef = createRef()
    }

    async componentDidMount() {
        this._isMounted = true

        try {
            const { hasError, error } = this.state

            if (hasError) Toast('error', error)

            this.calculateResultDistribution()
            this.autosizeAction('init', 'textarea-autosize')
        } catch (err) {
            console.error(err)

            Toast('error', {})
        }
    }

    componentWillUnmount() {
        this._isMounted = false
    }

    findFirstQuestionId(structure) {
        return !!structure.questions.length ? structure.questions[0].id : null
    }

    findFirstResultId(structure) {
        return !!structure.results.length ? structure.results[0].id : null
    }

    /**
     * Textarea autosize
     * @param {string} action
     * @param {string} className
     */
    autosizeAction = (action, className) => {
        try {
            const elements = document.getElementsByClassName(className)
            switch (action) {
                case 'init': {
                    autosize(elements)
                    break
                }
                case 'update': {
                    // if elements data or styles was change - we can update size manually
                    // but if we remove element from DOM (change tabs - for example), elements need re-init
                    autosize.update(elements)
                    break
                }
                default:
                    break
            }
        } catch (err) {
            console.error(err)

            Toast('error', {})
        }
    }

    onSave = async () => {
        try {
            const {
                methods,
                data: { id },
            } = this.props

            this.setState({
                saving: true,
            })

            const validationResult = await this.validateTabs()
            const hasError = !!validationResult.errors.length
            await this.setState(prevState => ({
                tabsValidationResult: {
                    ...prevState.tabsValidationResult,
                    onlyActiveTab: false,
                    showStopPanel: hasError,
                    errors: validationResult.errors,
                },
            }))
            if (!hasError) {
                validationResult.normalizedStructure._isValid = true

                await methods.save({
                    [id]: {
                        data: {
                            struct: validationResult.normalizedStructure,
                        },
                    },
                })

                methods.closeModal()
            }
        } catch (err) {
            console.error(err)

            Toast('error', {})
        } finally {
            if (this._isMounted) {
                this.setState({
                    saving: false,
                })
            }
        }
    }

    onClose = async () => {
        const {
            methods: { closeModal },
        } = this.props
        const { initialStructure } = this.state
        const { tmpStructure } = this.state
        const normalizedStructure = getNormalizedStructure(tmpStructure)

        if (!isEqual(initialStructure, normalizedStructure)) {
            await this.setState({ quitAcceptIsOpen: true })
        } else {
            closeModal()
        }
    }

    onExited = async () => {}

    onChangeTab = async tabIndex => {
        try {
            const onlyActiveTab = true
            const validationResult = await this.validateTabs(onlyActiveTab)

            if (validationResult.activeTabHasError) {
                await this.setState(prevState => ({
                    tabsValidationResult: {
                        ...prevState.tabsValidationResult,
                        onlyActiveTab,
                        showStopPanel: validationResult.activeTabHasError,
                        errors: validationResult.errors,
                    },
                }))
            } else {
                await this.setState({ activeTab: tabIndex })
                const { tmpStructure } = this.state
                switch (tabIndex) {
                    case TAB.questions:
                        await this.setState(() => ({
                            focusedQuestionId: this.findFirstQuestionId(tmpStructure),
                        }))
                        break
                    case TAB.results:
                        await this.setState(() => ({
                            focusedResultId: this.findFirstResultId(tmpStructure),
                        }))
                        break
                }
                this.autosizeAction('init', 'textarea-autosize')
            }
        } catch (err) {
            console.error(err)

            Toast('error', {})
        }
    }

    /**
     * Place image from AssetManager
     * @param {string} target
     * @param {object} value
     */
    onPlaceImage = async (target, value) => {
        try {
            const { tmpStructure } = this.state

            const _tmpStructure = cloneDeep(tmpStructure)

            set(_tmpStructure, target, value.src)

            await this.setState({
                tmpStructure: _tmpStructure,
            })
        } catch (err) {
            console.error(err)

            Toast('error', {})
        }
    }

    /**
     * Can do action (check as computed in render and in action function too)
     * @param {string} action
     * @param {string} target
     * @param {object} payload
     */
    canDo = (action, target, payload) => {
        switch (action) {
            case 'add': {
                switch (target) {
                    case 'questions': {
                        const {
                            tmpStructure: { questions },
                        } = this.state

                        if (questions.length < VALIDATION_RULES.maxQuestions) {
                            return true
                        }
                        break
                    }
                    case 'questions.answers': {
                        const {
                            tmpStructure: { questions },
                        } = this.state

                        if (questions[payload.qIndex].answers.length < VALIDATION_RULES.maxAnswersInQuestion) {
                            return true
                        }

                        break
                    }
                    case 'results': {
                        const {
                            tmpStructure: { questions, results },
                        } = this.state

                        if (questions.length + 1 > results.length) {
                            return true
                        }

                        break
                    }
                    default:
                        break
                }
                break
            }
            case 'changePosition': {
                switch (target) {
                    case 'questions': {
                        const {
                            tmpStructure: { questions },
                        } = this.state

                        if (payload.newIndex >= 0 && payload.newIndex <= questions.length - 1) {
                            return true
                        }

                        break
                    }
                    case 'questions.answers': {
                        break
                    }
                    case 'results': {
                        const {
                            tmpStructure: { results },
                        } = this.state

                        if (payload.newIndex >= 0 && payload.newIndex <= results.length - 1) {
                            return true
                        }

                        break
                    }
                    default:
                        break
                }
                break
            }
            case 'remove': {
                switch (target) {
                    case 'questions': {
                        const {
                            tmpStructure: { questions },
                        } = this.state

                        if (questions.length > VALIDATION_RULES.minQuestions) {
                            return true
                        }

                        break
                    }
                    case 'questions.answers': {
                        const {
                            tmpStructure: { questions },
                        } = this.state

                        if (questions[payload.qIndex].answers.length > VALIDATION_RULES.minAnswersInQuestion) {
                            return this.canDo(
                                'setAnswerCorrect',
                                {},
                                {
                                    value: payload.value,
                                    qIndex: payload.qIndex,
                                },
                            )
                        }
                        break
                    }
                    case 'results': {
                        const {
                            tmpStructure: { results },
                        } = this.state

                        if (results.length > VALIDATION_RULES.minResults) {
                            return true
                        }

                        break
                    }
                    default:
                        break
                }
                break
            }
            case 'setAnswerCorrect': {
                const {
                    tmpStructure: { questions },
                } = this.state

                if (payload.value) {
                    const all = questions[payload.qIndex].answers.length
                    const incorrectLength = questions[payload.qIndex].answers.filter(el => !el.isCorrect).length
                    if (all > 2 && incorrectLength - 1 < VALIDATION_RULES.minIncorrectAnswersInQuestion) {
                        return false
                    }
                } else {
                    const correctLength = questions[payload.qIndex].answers.filter(el => el.isCorrect).length
                    if (correctLength - 1 < VALIDATION_RULES.minCorrectAnswersInQuestion) {
                        return false
                    }
                }
                return true
            }
            default:
                break
        }
        return false
    }

    /**
     * Set default values
     * @param {any} data
     * @param {string} path
     */
    setDefaultValues = (data, path) => {
        const {
            structure: { defaultValues },
        } = SCHEMAS

        const _defaultValues = cloneDeep(defaultValues)

        for (const [key, value] of Object.entries(_defaultValues[path])) {
            set(data, key, value)
        }

        return data
    }

    /**
     * Add element
     * @param {string} target
     * @param {object} payload
     */
    onAdd = async (target, payload) => {
        try {
            if (this.canDo('add', target, payload)) {
                switch (target) {
                    case 'questions': {
                        const {
                            tmpStructure: { questions },
                        } = this.state

                        const _q = cloneDeep(questions)
                        let qData = this.createQuestion()
                        _q.push(qData)

                        await this.setState(prevState => ({
                            tmpStructure: {
                                ...prevState.tmpStructure,
                                questions: _q,
                            },
                            focusedQuestionId: qData.id,
                        }))
                        this.scrollContainerToQuestion(qData.id)
                        break
                    }
                    case 'questions.answers': {
                        const {
                            tmpStructure: { questions },
                        } = this.state
                        const {
                            structure: { schema },
                        } = SCHEMAS

                        const _q = cloneDeep(questions)

                        let aData = cloneDeep(schema.questions[0].answers[0])
                        aData.id = getRandomID(8)
                        aData = this.setDefaultValues(aData, 'questions.answers')

                        _q[payload.qIndex].answers.push(aData)

                        await this.setState(prevState => ({
                            tmpStructure: {
                                ...prevState.tmpStructure,
                                questions: _q,
                            },
                        }))

                        break
                    }
                    case 'results': {
                        const {
                            tmpStructure: { results },
                        } = this.state

                        const _r = cloneDeep(results)
                        let rData = this.createResult()

                        _r.push(rData)

                        await this.setState(prevState => ({
                            tmpStructure: {
                                ...prevState.tmpStructure,
                                results: _r,
                            },
                            focusedResultId: rData.id,
                        }))

                        this.scrollContainerToResult(rData.id)
                        break
                    }
                    default:
                        break
                }

                this.calculateResultDistribution()
                this.autosizeAction('init', 'textarea-autosize')
            }
        } catch (err) {
            console.error(err)

            Toast('error', {})
        }
    }

    /**
     * Clone element
     * @param {string} target
     * @param {object} payload
     */
    onClone = async (target, payload) => {
        try {
            if (this.canDo('add', target, payload)) {
                switch (target) {
                    case 'questions': {
                        const {
                            tmpStructure: { questions },
                        } = this.state

                        const _q = cloneDeep(questions)

                        const clonedElement = cloneDeep(questions[payload.index])
                        clonedElement.id = getRandomID(8)
                        clonedElement.answers = clonedElement.answers.map(el => {
                            return {
                                ...el,
                                id: getRandomID(8),
                            }
                        })

                        _q.splice(payload.newIndex, 0, clonedElement)

                        await this.setState(prevState => ({
                            tmpStructure: {
                                ...prevState.tmpStructure,
                                questions: _q,
                            },
                        }))

                        this.scrollContainerToQuestion(clonedElement.id)

                        break
                    }
                    case 'questions.answers': {
                        break
                    }
                    case 'results': {
                        const {
                            tmpStructure: { results },
                        } = this.state

                        const _r = cloneDeep(results)

                        const clonedElement = cloneDeep(results[payload.index])
                        clonedElement.id = getRandomID(8)

                        _r.splice(payload.newIndex, 0, clonedElement)

                        await this.setState(prevState => ({
                            tmpStructure: {
                                ...prevState.tmpStructure,
                                results: _r,
                            },
                        }))

                        this.scrollContainerToResult(clonedElement.id)

                        break
                    }
                    default:
                        break
                }

                this.calculateResultDistribution()
                this.autosizeAction('init', 'textarea-autosize')
            }
        } catch (err) {
            console.error(err)

            Toast('error', {})
        }
    }
    /**
     * Change element position
     * @param {string} target
     * @param {object} payload
     */
    onChangePosition = async (target, payload) => {
        try {
            if (this.canDo('changePosition', target, payload)) {
                switch (target) {
                    case 'questions': {
                        const {
                            tmpStructure: { questions },
                        } = this.state

                        let _q = cloneDeep(questions)
                        _q = moveInArray([..._q], payload.index, payload.newIndex)

                        await this.setState(prevState => ({
                            tmpStructure: {
                                ...prevState.tmpStructure,
                                questions: _q,
                            },
                        }))

                        break
                    }
                    case 'questions.answers': {
                        break
                    }
                    case 'results': {
                        const {
                            tmpStructure: { results },
                        } = this.state

                        let _r = cloneDeep(results)
                        _r = moveInArray([..._r], payload.index, payload.newIndex)

                        await this.setState(prevState => ({
                            tmpStructure: {
                                ...prevState.tmpStructure,
                                results: _r,
                            },
                        }))

                        break
                    }
                    default:
                        break
                }

                this.autosizeAction('init', 'textarea-autosize')
            }
        } catch (err) {
            console.error(err)

            Toast('error', {})
        }
    }
    /**
     * Remove element
     * @param {string} target
     * @param {object} payload
     */
    onRemove = async (target, payload) => {
        try {
            if (this.canDo('remove', target, payload)) {
                switch (target) {
                    case 'questions': {
                        const {
                            tmpStructure: { questions },
                        } = this.state

                        const _q = cloneDeep(questions)
                        _q.splice(payload.qIndex, 1)

                        await this.setState(prevState => ({
                            tmpStructure: {
                                ...prevState.tmpStructure,
                                questions: _q,
                            },
                        }))

                        break
                    }
                    case 'questions.answers': {
                        const {
                            tmpStructure: { questions },
                        } = this.state

                        const _q = cloneDeep(questions)

                        _q[payload.qIndex].answers.splice(payload.aIndex, 1)

                        await this.setState(prevState => ({
                            tmpStructure: {
                                ...prevState.tmpStructure,
                                questions: _q,
                            },
                        }))

                        break
                    }
                    case 'results': {
                        const {
                            tmpStructure: { results },
                        } = this.state

                        const _r = cloneDeep(results)
                        _r.splice(payload.rIndex, 1)

                        await this.setState(prevState => ({
                            tmpStructure: {
                                ...prevState.tmpStructure,
                                results: _r,
                            },
                        }))

                        break
                    }
                    default:
                        break
                }

                this.calculateResultDistribution()
                this.autosizeAction('init', 'textarea-autosize')
            }
        } catch (err) {
            console.error(err)

            Toast('error', {})
        }
    }

    createNewProject = () => {
        const {
            structure: { schema, version },
        } = SCHEMAS

        const data = cloneDeep(schema)
        data._v = version
        data._isValid = false
        data.questions = []
        data.results = []

        // set settings
        data._settings = this.setDefaultValues(data._settings, 'settings')
        // set cover
        data.cover = this.setDefaultValues(data.cover, 'cover')

        // set questions
        for (let i = 0; i < VALIDATION_RULES.minQuestions; i++) {
            let newQuestion = this.createQuestion()
            data.questions.push(newQuestion)
        }
        // set results
        for (let i = 0; i < VALIDATION_RULES.minResults; i++) {
            let newResult = this.createResult()
            data.results.push(newResult)
        }
        return data
    }

    createResult() {
        const {
            structure: { schema },
        } = SCHEMAS

        let rData = cloneDeep(schema.results[0])
        rData.id = getRandomID(8)
        return this.setDefaultValues(rData, 'results')
    }

    createQuestion() {
        const {
            structure: { schema },
        } = SCHEMAS

        let newQuestion = cloneDeep(schema.questions[0])
        newQuestion.id = getRandomID(8)
        newQuestion = this.setDefaultValues(newQuestion, 'questions')
        for (let i = 0; i < VALIDATION_RULES.minAnswersInQuestion; i++) {
            let aData = cloneDeep(schema.questions[0].answers[0])
            aData.id = getRandomID(8)
            aData = this.setDefaultValues(aData, 'questions.answers')
            if (VALIDATION_RULES.minCorrectAnswersInQuestion - i > 0) {
                aData.isCorrect = true
            }
            newQuestion.answers.push(aData)
        }
        return newQuestion
    }

    validateTabs = async (onlyActiveTab = false) => {
        // Note: Now we check only require param
        const { activeTab, tabsValidationResult } = this.state
        const { _fields } = VALIDATION_RULES
        const { tmpStructure } = this.state
        const normalizedStructure = getNormalizedStructure(tmpStructure)

        const getCoverErrors = () => {
            const errors = []
            for (const [key, value] of Object.entries(normalizedStructure.cover)) {
                const logic = _fields[`cover.${key}`]
                if (logic && logic.required) {
                    if (!value || !value.length) {
                        errors.push(`cover.${key}`)
                    }
                }
            }
            return errors
        }
        const getQuestionsErrors = () => {
            const errors = []
            for (let iQ = 0; iQ < normalizedStructure.questions.length; iQ++) {
                for (const [qKey, qValue] of Object.entries(normalizedStructure.questions[iQ])) {
                    if (qKey !== 'answers') {
                        const logic = _fields[`questions.${qKey}`]
                        if (logic && logic.required) {
                            if (!qValue || !qValue.length) {
                                errors.push(`questions[${iQ}].${qKey}`)
                            }
                        }
                    } else {
                        for (let iA = 0; iA < normalizedStructure.questions[iQ].answers.length; iA++) {
                            for (const [aKey, aValue] of Object.entries(
                                normalizedStructure.questions[iQ].answers[iA],
                            )) {
                                const logic = _fields[`questions.answers.${aKey}`]
                                if (
                                    logic &&
                                    (typeof logic.required === 'function'
                                        ? logic.required(normalizedStructure.questions[iQ])
                                        : logic.required)
                                ) {
                                    if (!aValue || !aValue.length) {
                                        errors.push(`questions[${iQ}].answers[${iA}].${aKey}`)
                                    }
                                }
                            }
                        }
                    }
                }
            }
            return errors
        }
        const getResultsErrors = () => {
            const errors = []
            for (let iR = 0; iR < normalizedStructure.results.length; iR++) {
                for (const [rKey, rValue] of Object.entries(normalizedStructure.results[iR])) {
                    const logic = _fields[`results.${rKey}`]
                    if (logic && logic.required) {
                        if (!rValue || !rValue.length) {
                            errors.push(`results[${iR}].${rKey}`)
                        }
                    }
                }
            }
            return errors
        }

        let errors = []

        const returnPayload = {
            activeTabHasError: false,
        }

        if (onlyActiveTab) {
            errors = cloneDeep(tabsValidationResult.errors)
            switch (activeTab) {
                case TAB.start: {
                    const coverErrors = getCoverErrors()
                    returnPayload.activeTabHasError = !!coverErrors.length
                    errors = [...errors, ...coverErrors]
                    break
                }
                case TAB.questions: {
                    const questionsErrors = getQuestionsErrors()
                    returnPayload.activeTabHasError = !!questionsErrors.length
                    errors = [...errors, ...questionsErrors]
                    break
                }
                case TAB.results: {
                    const resultsErrors = getResultsErrors()
                    returnPayload.activeTabHasError = !!resultsErrors.length
                    errors = [...errors, ...resultsErrors]
                    break
                }
                default:
                    break
            }
            errors = [...new Set(errors)]
        } else {
            errors = [...getCoverErrors(), ...getQuestionsErrors(), ...getResultsErrors()]
        }

        return {
            normalizedStructure,
            errors,
            ...returnPayload,
        }
    }

    /**
     * Change temporary structure
     * @param {string} target
     * @param {any|undefined} value
     * @param {boolean} preventNewLine
     */
    handleChange__tmpStructure = async (target, value, preventNewLine = false) => {
        try {
            const {
                tmpStructure,
                tabsValidationResult: { errors },
            } = this.state

            const _tmpStructure = cloneDeep(tmpStructure)

            set(_tmpStructure, target, preventNewLine ? value.replace(/\n/g, '') : value)

            await this.setState(prevState => ({
                tmpStructure: _tmpStructure,
                tabsValidationResult: {
                    ...prevState.tabsValidationResult,
                    errors: errors.filter(el => el !== target),
                },
            }))

            this.autosizeAction('update', 'textarea-autosize')
        } catch (err) {
            console.error(err)
        }
    }

    onSetAnswerCorrect = async (qIndex, aIndex, value) => {
        try {
            const { tmpStructure } = this.state

            const _tmpStructure = cloneDeep(tmpStructure)

            const _answers = cloneDeep(tmpStructure.questions[qIndex].answers)
            const correctLength = _answers.filter(el => el.isCorrect).length
            const incorrectLength = _answers.length - correctLength
            if (value) {
                if (incorrectLength - 1 < VALIDATION_RULES.minIncorrectAnswersInQuestion) {
                    const i = _answers.findIndex(el => el.isCorrect)
                    if (i !== -1) {
                        set(_tmpStructure, `questions[${qIndex}].answers[${i}].isCorrect`, false)
                    }
                }
            } else if (correctLength - 1 < VALIDATION_RULES.minCorrectAnswersInQuestion) return

            set(_tmpStructure, `questions[${qIndex}].answers[${aIndex}].isCorrect`, value)

            await this.setState({
                tmpStructure: _tmpStructure,
            })
        } catch (err) {
            console.error(err)
        }
    }

    calculateResultDistribution = () => {
        try {
            const { tmpStructure } = this.state

            const _tmpStructure = cloneDeep(tmpStructure)

            const maxNumberOfOptions = _tmpStructure.questions.length + 1
            const numberOfResults = _tmpStructure.results.length

            const res = []
            if (maxNumberOfOptions % numberOfResults === 0) {
                const step = maxNumberOfOptions / numberOfResults
                let from = 0
                for (let i = 0; i < numberOfResults; i++) {
                    if (i <= maxNumberOfOptions - 1) {
                        const to = i !== numberOfResults - 1 ? from + step - 1 : maxNumberOfOptions - 1
                        res.push({
                            isVisible: true,
                            from: from,
                            to: to,
                        })
                        from = to + 1
                    } else {
                        res.push({
                            isVisible: false,
                        })
                    }
                }
            } else {
                let checkChain = true
                let step = Math.ceil(maxNumberOfOptions / numberOfResults)
                let from = 0
                for (let i = 0; i < numberOfResults; i++) {
                    if (i <= maxNumberOfOptions - 1) {
                        if (checkChain && (maxNumberOfOptions - i) % numberOfResults === 0) {
                            step = (maxNumberOfOptions - i) / numberOfResults
                            checkChain = false
                        }
                        const to = from + step - 1
                        res.push({
                            isVisible: true,
                            from: from,
                            to: to,
                        })
                        from = to + 1
                    } else {
                        res.push({
                            isVisible: false,
                        })
                    }
                }
            }

            set(_tmpStructure, '_settings.distribution', res)

            this.setState({
                tmpStructure: _tmpStructure,
            })
        } catch (err) {
            console.error(err)
        }
    }

    scrollContainerToQuestion = id => {
        this.scrollContainerToElement(this.scrollableQuestionContentNodeRef, id)
    }

    scrollContainerToResult = id => {
        this.scrollContainerToElement(this.scrollableResultContentNodeRef, id)
    }

    scrollContainerToElement = (nodeRef, elementId) => {
        try {
            let element = document.getElementById(elementId)
            nodeRef.current.scrollTo({
                top: element.offsetTop - FIXED_TOP_OFFSET_FOR_SCROLL,
                left: 0,
                behavior: 'smooth',
            })
        } catch (err) {
            console.error(err)
        }
    }

    setFocusToQuestion = async questionElement => {
        const { focusedQuestionId } = this.state
        let questionId = questionElement.id
        if (questionId !== focusedQuestionId) await this.setState({ focusedQuestionId: questionId })
    }

    setFocusToResult = async resultElement => {
        const { focusedResultId } = this.state
        let resultId = resultElement.id
        if (resultId !== focusedResultId) await this.setState({ focusedResultId: resultId })
    }

    onClickNavigateToQuestion = async questionId => {
        const { focusedQuestionId } = this.state
        if (focusedQuestionId === questionId) return
        this.scrollContainerToQuestion(questionId)
    }

    onClickNavigateToResult = async resultId => {
        const { focusedResultId } = this.state
        if (focusedResultId === resultId) return
        this.scrollContainerToResult(resultId)
    }

    render() {
        const {
            saving,
            ready,
            tmpStructure,
            activeTab,
            focusedQuestionId,
            focusedResultId,
            tabsValidationResult,
            quitAcceptIsOpen,
        } = this.state
        const {
            methods,
            data: { colorTheme },
            t,
        } = this.props

        let tabContent = null
        switch (activeTab) {
            case TAB.start: {
                tabContent = (
                    <StartScreenView
                        data={{
                            colorTheme: colorTheme,
                        }}
                        tmpStructure={tmpStructure}
                        validationStructure={VALIDATION_RULES}
                        tabsValidationResult={tabsValidationResult}
                        methods={{
                            openAssetManager: methods.openAssetManager,
                            handleChange__tmpStructure: this.handleChange__tmpStructure,
                            onPlaceImage: this.onPlaceImage,
                        }}
                    />
                )
                break
            }
            case TAB.questions: {
                tabContent = (
                    <>
                        <aside>
                            <NavigationListBar
                                titleOfItem={t('Question')}
                                focusedItemId={focusedQuestionId}
                                items={tmpStructure.questions}
                                maxLength={VALIDATION_RULES.maxQuestions}
                                onClickItem={this.onClickNavigateToQuestion}
                                onClickAddNew={() => this.onAdd('questions')}
                            />
                        </aside>
                        <div className="body">
                            <div className="tab-wrapper tab-wrapper--2">
                                <div className="workplace">
                                    <SimpleBar
                                        autoHide={false}
                                        style={{ maxHeight: '100%' }}
                                        scrollableNodeProps={{ ref: this.scrollableQuestionContentNodeRef }}
                                    >
                                        <div className="scrollbar-inner">
                                            <QuestionList
                                                tmpStructure={tmpStructure}
                                                validationStructure={VALIDATION_RULES}
                                                tabsValidationResult={tabsValidationResult}
                                                methods={{
                                                    openAssetManager: methods.openAssetManager,
                                                    handleChange__tmpStructure: this.handleChange__tmpStructure,
                                                    canDo: this.canDo,
                                                    onChangePosition: this.onChangePosition,
                                                    onClone: this.onClone,
                                                    onRemove: this.onRemove,
                                                    onAdd: this.onAdd,
                                                    onSetAnswerCorrect: this.onSetAnswerCorrect,
                                                    onPlaceImage: this.onPlaceImage,
                                                    setFocusToQuestion: this.setFocusToQuestion,
                                                }}
                                            />
                                        </div>
                                    </SimpleBar>
                                </div>
                            </div>
                        </div>
                    </>
                )
                break
            }
            case TAB.results: {
                tabContent = (
                    <>
                        <aside>
                            <NavigationListBar
                                titleOfItem={t('Result')}
                                focusedItemId={focusedResultId}
                                items={tmpStructure.results}
                                maxLength={tmpStructure.questions.length + 1}
                                onClickItem={this.onClickNavigateToResult}
                                onClickAddNew={() => this.onAdd('results')}
                            />
                        </aside>
                        <div className="body">
                            <div className="tab-wrapper tab-wrapper--3">
                                <div className="workplace">
                                    <SimpleBar
                                        autoHide={false}
                                        style={{ maxHeight: '100%' }}
                                        scrollableNodeProps={{ ref: this.scrollableResultContentNodeRef }}
                                    >
                                        <div className="scrollbar-inner">
                                            <ResultList
                                                tmpStructure={tmpStructure}
                                                validationStructure={VALIDATION_RULES}
                                                tabsValidationResult={tabsValidationResult}
                                                methods={{
                                                    openAssetManager: methods.openAssetManager,
                                                    onPlaceImage: this.onPlaceImage,
                                                    handleChange__tmpStructure: this.handleChange__tmpStructure,
                                                    canDo: this.canDo,
                                                    onChangePosition: this.onChangePosition,
                                                    onClone: this.onClone,
                                                    onRemove: this.onRemove,
                                                    onAdd: this.onAdd,
                                                    setFocusToResult: this.setFocusToResult,
                                                }}
                                            />
                                        </div>
                                    </SimpleBar>
                                </div>
                            </div>
                        </div>
                    </>
                )
                break
            }
        }

        return (
            <>
                {ready ? (
                    <Modal closeOnOverlayClick={false} closeOnEsc={false}>
                        <Header
                            title={t('Quiz options')}
                            navItems={TRIVIA_TABS_ARRAY}
                            activeNavItemId={activeTab}
                            onChangeNavItem={id => this.onChangeTab(id)}
                            onClose={() => this.onClose()}
                        />
                        <div className="quiz-modal-wrapper">{tabContent}</div>
                        <Footer
                            actionText={t('Save')}
                            cancelText={t('Cancel')}
                            onClose={() => this.onClose()}
                            onAction={() => this.onSave()}
                        />

                        {tabsValidationResult.showStopPanel && (
                            <StopPanel
                                t={t}
                                headText={
                                    tabsValidationResult.onlyActiveTab ? t('Oh! Need more information') : t('Oh, no!')
                                }
                                descriptionText={
                                    tabsValidationResult.onlyActiveTab
                                        ? t(
                                              'Please fill all required fields on this tab for the quiz to work correctly.',
                                          )
                                        : t('Please fill all required fields for the quiz to work correctly.')
                                }
                                image={iconGirl}
                                onBack={() => {
                                    this.setState(prevState => ({
                                        tabsValidationResult: {
                                            ...prevState.tabsValidationResult,
                                            showStopPanel: false,
                                        },
                                    }))
                                }}
                                isShowQuit={false}
                            />
                        )}
                        {quitAcceptIsOpen && (
                            <StopPanel
                                t={t}
                                isLoading={saving}
                                onBack={() => this.setState({ quitAcceptIsOpen: false })}
                                onQuit={methods.closeModal}
                            />
                        )}
                    </Modal>
                ) : null}
            </>
        )
    }
}

export default withTranslation('translations')(TriviaQuizModal)
